import { Nullable } from '@infotech-soe/merps/types/common';
import {
  IContextMenuGroupItem,
  IContextMenuItem,
  TApplicationName,
  TDatabaseId,
  TDatasetName,
} from '@infotech-soe/metaschema';
import { filter, omit } from 'lodash';

import CachedSchemaRepository from '../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../infrastructure/schemas/storage/ISchemaRepository';
import { CategoryTreeStateEntity } from '../../models/category/categoryTree';
import CategoryInParamsExtractorService from '../category/CategoryInParamsExtractorService';
import ICategoryInParamsExtractorService from '../category/ICategoryInParamsExtractorService';
import CategoryViewModel from '../category/view-model/CategoryViewModel';
import ContextService from '../context/ContextService';
import IContextService, { ClientContext } from '../context/IContextService';
import { DatasetCurrentState } from '../dataset/view-model/DatasetCurrentState';
import UniqueId from '../uid/UniqueId';

import CommandComponent from './command-items/CommandComponent';
import CommandItem from './command-items/CommandItem';
import CommandBuilder, { NamedAction } from './CommandBuilder';
import CommandPrescript from './CommandPrescript';
import CommandViewModel, { CommandListEntryType } from './CommandViewModel';
import { CommandsNameKeyMap } from './constants';
import ICommandService, {
  CommandExecuteBatchResult,
  CommandExecuteSingleResult,
} from './ICommandService';
import CategoryCommand from './wrap/CategoryCommand';

const DEFAULT_TEMPLATE_NAME = 'Default';

class CommandServiceSingleton implements ICommandService {
  private cacheMap: Record<
    TDatasetName,
    { commands: CommandComponent[]; hooks: CommandComponent[] }
  > = {};

  private readonly schemaRepository: ISchemaRepository;
  private readonly contextService: IContextService;
  private readonly inParamsExtractor: ICategoryInParamsExtractorService;
  constructor() {
    this.schemaRepository = new CachedSchemaRepository();
    this.contextService = new ContextService();
    this.inParamsExtractor = new CategoryInParamsExtractorService();
  }

  public async executeCommand(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId,
    commandName: string,
    args: any,
    application: TApplicationName,
    hook?: boolean
  ): Promise<void | CommandExecuteBatchResult | CommandExecuteSingleResult> {
    const command = hook
      ? await this.findDatasetHook(
          category,
          datasetsState,
          datasetId,
          commandName
        )
      : await this.findDatasetCommand(
          category,
          datasetsState,
          datasetId,
          commandName,
          application
        );

    const context = await this.contextService.getContext(
      category,
      datasetsState,
      datasetId
    );

    if (
      command.getProcessMode() !== 'Sequential' ||
      !context.has('SelectedIds')
    ) {
      try {
        const results = (await command.execute(context, args)) || {};
        const inParams = await this.inParamsExtractor.fromCommand(
          context,
          command.getName(),
          results
        );

        return {
          ...(inParams ? omit(results, Object.keys(inParams)) : results),
          inParams: inParams ?? {},
          refresh: command.getRefreshStrategy(),
          refreshBuilder: command.getRefreshStrategyBuilder(),
          refreshRules: command.getRefreshRules(),
        };
      } catch (error: any) {
        return {
          error,
          refresh: command.getRefreshStrategy(),
        };
      }
    }

    const result: CommandExecuteBatchResult = {
      results: new Map<TDatabaseId, Record<string, any>>(),
      error: null,
      refresh: command.getRefreshStrategy(),
      refreshBuilder: command.getRefreshStrategyBuilder(),
      refreshRules: command.getRefreshRules(),
    };
    const ctx = new ClientContext(context);
    ctx.delete('SelectedIds');
    for (const id of context.get('SelectedIds')) {
      ctx.set('Id', id);
      try {
        result.results.set(id, (await command.execute(ctx, args)) || {});
      } catch (error: any) {
        result.error = error;
        break;
      }
    }
    return result;
  }

  public async getCommands(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId,
    application: Nullable<TApplicationName>,
    treeState?: CategoryTreeStateEntity
  ): Promise<{ commands: CommandViewModel[]; keyMap: CommandsNameKeyMap }> {
    const { commands } = await this.getCommandsForDataset(
      category,
      datasetsState,
      datasetId,
      application
    );
    const context = await this.contextService.getContext(
      category,
      datasetsState,
      datasetId,
      treeState
    );

    const keyMap: CommandsNameKeyMap = {};
    const serializedCommands: CommandViewModel[] = [];

    const getKeyMap = (commandList: CommandViewModel[]): void => {
      for (const commandEntry of commandList) {
        if (
          commandEntry.type === CommandListEntryType.COMMAND &&
          commandEntry.available
        ) {
          const key = commandEntry.hotKey;
          if (key) {
            keyMap[key] = commandEntry.name;
          }
        } else if (
          commandEntry.type === CommandListEntryType.GROUP &&
          commandEntry.children.length > 0
        ) {
          getKeyMap(commandEntry.children);
        }
      }
    };

    for (const command of commands) {
      const serializedCommand = await command.serialize(context);
      getKeyMap([serializedCommand]);
      serializedCommands.push(serializedCommand);
    }

    return {
      commands: serializedCommands,
      keyMap,
    };
  }

  public async initiateCommand(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId,
    commandName: string,
    application: TApplicationName,
    hook?: boolean
  ): Promise<CommandPrescript<any>> {
    const entry = hook
      ? await this.findDatasetHook(
          category,
          datasetsState,
          datasetId,
          commandName
        )
      : await this.findDatasetCommand(
          category,
          datasetsState,
          datasetId,
          commandName,
          application
        );

    const context = await this.contextService.getContext(
      category,
      datasetsState,
      datasetId
    );

    const currentRecord = datasetsState[datasetId].rows.find(
      row => row[datasetsState[datasetId].rowIdField] === context.get('Id')
    );

    return {
      interactor: await entry.getUserInteractor(),
      initialArgs: await entry.getInitialArgs(context, currentRecord),
    };
  }

  private mergeTemplate(
    items: IContextMenuItem[],
    overrides: any
  ): IContextMenuItem[] {
    const overs = overrides || {};
    const merged: IContextMenuItem[] = [];
    const keys = Object.keys(overs);
    for (const key of keys) {
      if (typeof overs[key] === 'string') {
        overs[key] = { action: overs[key] };
      }
    }
    for (const item of items) {
      if (item.kind === 'Substitute') {
        if (keys.includes(item.name)) {
          merged.push(...overs[item.name]);
        }
        continue;
      }
      if (item.kind === 'Group') {
        const childItems = this.mergeTemplate(
          (item as IContextMenuGroupItem).items,
          overs
        );
        if (childItems.length > 0) {
          merged.push({ ...item, items: childItems } as IContextMenuGroupItem);
        }
        continue;
      }
      if (keys.includes(item.kind)) {
        merged.push({ ...item, ...overs[item.kind] });
      } else {
        merged.push(item);
      }
    }
    return merged;
  }

  private async findDatasetCommand(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId,
    commandName: string,
    application: TApplicationName
  ): Promise<CategoryCommand<any>> {
    const { commands } = await this.getCommandsForDataset(
      category,
      datasetsState,
      datasetId,
      application
    );

    const found = this.findCommand(commands, commandName);
    if (found) {
      return found;
    }
    throw new Error(`Command[${commandName}] not found`);
  }

  private async findDatasetHook(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId,
    commandName: string
  ): Promise<CategoryCommand<any>> {
    const { hooks: commands } = await this.getCommandsForDataset(
      category,
      datasetsState,
      datasetId,
      null
    );

    const found = this.findCommand(commands, commandName);
    if (found) {
      return found;
    }
    throw new Error(`Hook[${commandName}] not found`);
  }

  private findCommand(
    commands: CommandComponent[],
    commandName: string
  ): CategoryCommand<any> | void {
    for (const command of commands) {
      const found = command.find(commandName);
      if (found) {
        return (found as CommandItem<any>).getCommand();
      }
    }
  }

  private async getCommandsForDataset(
    category: CategoryViewModel,
    datasetsState: Record<UniqueId, DatasetCurrentState>,
    datasetId: UniqueId,
    application: Nullable<TApplicationName>
  ): Promise<{ commands: CommandComponent[]; hooks: CommandComponent[] }> {
    if (!this.cacheMap[datasetId]) {
      const context = await this.contextService.getContext(
        category,
        datasetsState,
        datasetId
      );

      const datasetName = category.datasets[datasetId].name;

      const displayMode = await this.schemaRepository.getDisplayMode(
        category.source.category,
        category.source.displayMode
      );
      const menuSchema = displayMode.datasets[datasetName].menu;
      const templateName =
        menuSchema && menuSchema.template
          ? menuSchema.template
          : DEFAULT_TEMPLATE_NAME;
      const template = await this.schemaRepository.getTemplate(
        'menus',
        templateName
      );
      const items = this.mergeTemplate(
        template.items,
        displayMode.datasets[datasetName].menu?.overrides
      );
      const actions: NamedAction[] = [];
      const actionNames = await this.schemaRepository.getListCategoryActions(
        context.get('Category')
      );
      for (const name of actionNames) {
        const action = await this.schemaRepository.getAction(
          context.get('Category'),
          name
        );
        actions.push({
          name,
          action,
        });
      }

      const hooks = filter([
        displayMode.beforeOpen &&
          displayMode.beforeOpen.kind === 'action' &&
          displayMode.beforeOpen.action,
        displayMode.afterClose && displayMode.afterClose.action,
        displayMode.afterCancel && displayMode.afterCancel.action,
      ]) as string[];

      const builder = new CommandBuilder(
        items,
        category,
        context as ClientContext,
        datasetId,
        actions,
        datasetsState,
        hooks,
        application
      );

      this.cacheMap[datasetId] = {
        commands: await builder.buildCommandsForDataset(),
        hooks: await builder.buildHooksForDisplayMode(),
      };
    }

    return this.cacheMap[datasetId];
  }

  public async purgeCache(): Promise<void> {
    this.cacheMap = {};
  }

  public clearCacheBy(categoryId: string): void {
    delete this.cacheMap[categoryId];
  }
}

const CommandService = (() => {
  let instance: ICommandService;
  return new Proxy(CommandServiceSingleton, {
    construct(Target: typeof CommandServiceSingleton): ICommandService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default CommandService;
